import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { EMPTY_LIST, EMPTY_OBJECT } from '../../../../constants/common'
import { useNotification, useSelection } from '../../../../hooks'
import Button from '../../../Button'
import { useFileUpload } from '../../../File'
import {
  getDropResultForUpdateRowsPosition,
  getUpdatedDraggedRowsPosition,
  useAsyncTable,
} from '../../../Table'
import { getFileUploadedIds } from '../../DocFlow.utils.files'
import { DocFlowAttachFilesButton } from '../../DocFlowAttachFilesButton'
import {
  getUploadingTableApiUrls,
  isNextPhaseDisabled,
  isUploadingButtonDisabled,
} from '../../DocFlowUploading'
import { useDocFlowContext } from '../useDocFlowContext'
import { useGetUploadingFiles } from '../useGetUploadingFiles'
import { useUploadingTableActions } from '../useUploadingTableActions'

import {
  getPreparedUploadingFiles,
  getUploadedFilesAfterReload,
  getUploadingFilesAfterUploaded,
} from './useUploadingPhase.utils'

export function useUploadingPhase() {
  const {
    docFlow,
    documentId,
    handleCloseModal,
    handleReloadDocFlow,
    handleUpdateDocFlow,
    setFooterProps,
    setModal,
  } = useDocFlowContext()

  const { t } = useTranslation()

  const [uploadingFiles, setUploadingFiles] = useState(EMPTY_LIST)
  const [uploadedFiles, setUploadedFiles] = useState(EMPTY_LIST)
  const [isUploading, setUploading] = useState(false)

  const [updatedRows, setUpdatedRows] = useState(null)

  const acceptedFilesRef = useRef(EMPTY_LIST)
  const errorFilesRef = useRef(EMPTY_LIST)

  const { handleAddErrorNotification, handleAddSuccessNotification } =
    useNotification()

  const tableApi = getUploadingTableApiUrls(documentId)

  const uploadingTableLoader = useAsyncTable({
    ...tableApi,
    withoutSubMenu: true,
  })

  const {
    filterValues,
    handleLoadInit,
    handleReload,
    isInitiated,
    items,
    ordering,
    pagination,
  } = uploadingTableLoader

  const uploadingDocuments = items?.items || EMPTY_LIST

  const getUploadingFilesParams = isInitiated
    ? { filters: filterValues, ordering }
    : EMPTY_OBJECT

  const selectionData = useSelection({ data: items, pagination })

  const {
    handleInitUploadingFilesAll,
    handleReloadUploadingFilesAll,
    uploadingFiles: allFiles,
  } = useGetUploadingFiles({
    canLoad: isInitiated,
    params: getUploadingFilesParams,
  })

  const { handleOpenChangeTypeModal, setItemAction, setMassAction } =
    useUploadingTableActions({
      documentId,
      excludeIds: selectionData.excludeIds,
      files: allFiles,
      filterValues,
      pagination,
      selectAllPages: selectionData.selectAllPages,
      selectedIds: selectionData.selectedIds,
      onCleanSelection: selectionData.handleCleanSelection,
      onCloseModal: handleCloseModal,
      onLoadAllFiles: handleInitUploadingFilesAll,
      onReload: handleReload,
      onReloadDocFlow: handleReloadDocFlow,
      onSetModal: setModal,
      onUpdateDocFlow: handleUpdateDocFlow,
    })

  function handleReloadTable(ids) {
    handleReload().then(() => {
      handleAddSuccessNotification({
        title: t('Notification.CommonTitleAttachedFilesKey', {
          count: acceptedFilesRef.current.length,
          totalCount:
            acceptedFilesRef.current.length + errorFilesRef.current.length,
        }),
      })

      setUploading(false)

      setUploadingFiles(files =>
        getUploadingFilesAfterUploaded(files, uploadedFiles)
      )

      setUploadedFiles(files => getUploadedFilesAfterReload(files, ids))
    })
  }

  function handleUpdateFilesForDocFlow() {
    const ids = allFiles
      .concat(uploadedFiles)
      .map(item => item.file_id || item.id)

    handleUpdateDocFlow(
      {
        file_ids: ids,
      },
      null
    )
      .then(() => handleReloadTable(ids))
      .then(handleInitUploadingFilesAll)
  }

  function handleUploadFiles(getNewUploadedFiles) {
    const newUploadedFiles = getNewUploadedFiles(uploadingDocuments)

    const ids = newUploadedFiles.filter(item => item.id && !item.file_id)

    setUploadedFiles(prev => [...prev, ...ids])
  }

  const { handleStartUploading, handleUploadErrors, handleUploadFile } =
    useFileUpload({
      files: uploadingDocuments,
      onSetFiles: handleUploadFiles,
    })

  const totalFilesCount = allFiles?.length

  const tableItems = uploadingDocuments.concat(uploadingFiles)

  const previewFiles = uploadingDocuments.map(file => ({
    ...file,
    id: file.file_id,
  }))

  const nextPhaseDisabled = isNextPhaseDisabled(docFlow)

  function handleFinishUpload(acceptedFiles, errorFiles) {
    if (acceptedFiles.length > 0) {
      handleUploadFile(acceptedFiles)

      errorFilesRef.current = errorFiles
      acceptedFilesRef.current = acceptedFiles
    } else if (errorFiles.length > 0) {
      handleAddErrorNotification(
        'Notification.CommonTitleAttachedFilesErrorKey'
      )
    }
  }

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional state update tied to the upload lifecycle
    setUpdatedRows(null)
  }, [uploadingDocuments])

  function handleStartUploadingFiles(newfiles) {
    setUploadingFiles(prev => [...prev, ...getPreparedUploadingFiles(newfiles)])

    handleStartUploading(newfiles)
  }

  useEffect(() => {
    handleLoadInit()
  }, [])

  useEffect(() => {
    if (!isUploading && uploadedFiles.length > 0) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional state update tied to the upload lifecycle
      setUploading(true)
      handleUpdateFilesForDocFlow()
    }
  }, [isUploading, uploadedFiles])

  function handleDragEnd(event) {
    const activeId = event.active?.id
    const overId = event.over?.id

    if (!activeId || !overId || String(activeId) === String(overId)) {
      return
    }

    const result = getDropResultForUpdateRowsPosition({
      activeId,
      items: allFiles,
      overId,
    })

    if (!result) {
      return
    }

    const updatedDraggedRows = getUpdatedDraggedRowsPosition({
      itemPositionKey: 'position',
      result,
      rows: allFiles,
    })

    if (updatedDraggedRows) {
      const newRows = updatedDraggedRows.sort((a, b) => a.position - b.position)

      setUpdatedRows(newRows.slice(0, pagination.pageSize))

      handleUpdateDocFlow(
        {
          file_ids: getFileUploadedIds(newRows),
        },
        null
      ).then(() => {
        handleReload()
        handleReloadUploadingFilesAll()
      })
    }
  }

  const headerActionsProps = {
    archiveButtonProps: {
      disabled: uploadingFiles.length > 0,
    },
    attachDisabled: isUploadingButtonDisabled(docFlow),
    files: tableItems,
    nextPhaseDisabled,
    tooltip: 'DocumentStapling.UploadingPhaseNextPhaseDisabledBtnTooltipKey',
    totalFilesCount: totalFilesCount,
    onFinishUpload: handleFinishUpload,
    onStartUpload: handleStartUploadingFiles,
    onUploadErrors: handleUploadErrors,
  }

  useEffect(() => {
    if (isInitiated) {
      setFooterProps(headerActionsProps)
    }
  }, [headerActionsProps, isInitiated])

  const emptyStateActions = docFlow?.archived
    ? null
    : [
        {
          ...headerActionsProps,
          Button: Button.Contained,
          Component: DocFlowAttachFilesButton,
          name: 'add-new-stapling',
        },
      ]

  return {
    emptyStateActions,
    files: uploadingDocuments,
    handleDragEnd,
    handleOpenChangeTypeModal,
    previewFiles,
    selectionData,
    setItemAction,
    setMassAction,
    tableItems,
    updatedRows,
    uploadingTableLoader,
  }
}
